import {
  ActionType,
  extractMentionsFromText,
  extractTitleFromMessage,
  getIssueTypeFromSeverity,
  IssueCreationSource,
  IssueType,
  getDeviceAtTimeFromInstant,
  getScheduleDefaultInstant,
  Media,
  MediaPickerComponent,
  MediaType,
  parseMMDDYYYYToDate,
  PickerMode,
  ReportIssueInput,
  SCHEDULE_DEFAULTS,
  Severity,
  TriggerType,
  useCreateIssueWithMessage,
  useCreateMutation,
  useCurrentLocation,
  useWorkspaceLabels,
} from '@gripp/shared-logic';

import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { InferType } from 'yup';
import { Layout } from '../../styles';
import { SaveFormBottom, SaveFormTop } from '../forms';
import { KeyboardSafeView } from '../keyboard/keyboardSafeView';
import { HorizontalMediaGallery } from '../media';
import { BottomComponent, TopComponent } from '../navigation';
import {
  NewIssueReportForm,
  NewIssueReportFormSchema,
} from './newIssueReportForm';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

const CREATE_WORKFLOW = `
  mutation createWorkflow($input: CreateWorkflowInput!) {
    createWorkflow(input: $input) {
      id
      name
    }
  }
`;

const CREATE_LABEL = `
  mutation CreateLabel($input: CreateLabelInput!) {
    createLabel(input: $input) {
      id
      name
    }
  }
`;

type NewIssueProps = {
  assetId?: string;
  workspaceId: string;
  mediaPicker: MediaPickerComponent;
  acceptedMediaTypes?: MediaType[];
  onIssueCreated: (id: string, type?: IssueType) => void;
  onCancel?: () => void;
  topBar: TopComponent;
  mode?: PickerMode;
  createdFrom?: IssueCreationSource;
  hideAdvancedSettings?: boolean;
  pickerMode?: 'picker' | 'camera';
  bottomBar?: BottomComponent;
  onSelectorEdit?: () => void;
  selectedLabels?: Array<{ id: string; name: string }>;
  onLabelsChange?: (labels: Array<{ id: string; name: string }>) => void;
};

type NewIssueReportFormData = InferType<typeof NewIssueReportFormSchema>;

export const NewIssue = (props: NewIssueProps) => {
  const { assetId, workspaceId, createdFrom } = props;
  const MediaPicker = props.mediaPicker;
  const acceptedMediaTypes = props.acceptedMediaTypes ?? [
    MediaType.Image,
    MediaType.Video,
  ];
  const { mutateAsync, isPending } = useCreateIssueWithMessage();
  const { getLocation } = useCurrentLocation();
  const { t } = useTranslation();
  const [issueMedia, setIssueMedia] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Fetch labels from current workspace
  const { labels: availableLabels } = useWorkspaceLabels(workspaceId);

  // Create label mutation
  const { mutateAsync: createLabel } = useCreateMutation({
    modelName: 'label',
    query: CREATE_LABEL,
  });

  const handleCreateLabel = async (name: string) => {
    try {
      const result = await createLabel({
        input: { name, workspace: { id: workspaceId } },
      });
      return result;
    } catch (error) {
      console.error('Failed to create label:', error);
      throw error;
    }
  };

  const { mutateAsync: createWorkflow } = useCreateMutation({
    modelName: 'workflow',
    query: CREATE_WORKFLOW,
  });

  const issueTypeTitle = t('issue.new.addIt');

  const addMedia = (newMediaItem: Media) => {
    if (!newMediaItem) return;
    setIssueMedia((prevState) => [...prevState, newMediaItem.id]);
  };

  const deleteMedia = (mediaId: string) => {
    setIssueMedia((prevState) => prevState.filter((id) => id !== mediaId));
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (formData: NewIssueReportFormData) => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const location = await getLocation();

      const issueType = getIssueTypeFromSeverity(
        formData.activityType as Severity
      );

      const mentions = extractMentionsFromText(formData.message);

      const input: ReportIssueInput = {
        severity: formData.activityType as Severity,
        title: formData.title || extractTitleFromMessage(formData.message),
        type: issueType,
        message: formData.message,
        workspaceId: workspaceId,
        media: issueMedia.length > 0 ? issueMedia : undefined,
        location,
        createdFrom,
        labelIds: formData.labels?.map((label) => label.id),
        userMentions:
          mentions.length > 0 ? mentions.map((m) => m.id as string) : undefined,
        adjustCreationDate: formData.adjustCreationDate
          ? parseMMDDYYYYToDate(String(formData.adjustCreationDate))
          : undefined,
      };

      if (assetId) input.assetId = assetId;

      await mutateAsync(
        { input },
        {
          onSettled: async (data: {
            issue: { reportIssue: { id: string } };
          }) => {
            props.onIssueCreated(data.issue.reportIssue.id, issueType);

            if (formData.quickReminderDate) {
              const parsedDate = parseMMDDYYYYToDate(
                String(formData.quickReminderDate)
              );
              const reminderConfig: Record<string, unknown> = {
                issueId: data.issue.reportIssue.id,
                onDate: parsedDate,
              };

              // Only write atTime/timeZone once the user has actually moved the
              // pre-fill — an untouched value stays default-driven.
              const chosenTime = formData.quickReminderTime;
              if (
                typeof chosenTime === 'string' &&
                chosenTime &&
                chosenTime !== quickReminderTimeBaseline
              ) {
                const { atTime, timeZone } =
                  getDeviceAtTimeFromInstant(chosenTime);
                reminderConfig.atTime = atTime;
                reminderConfig.timeZone = timeZone;
              }

              const workflowInput = {
                name: `${t('issue.reminderFor')} ${
                  formData.title || formData.message
                }`,
                workspace: { id: workspaceId },
                trigger: {
                  type: TriggerType.Issue,
                  config: reminderConfig,
                },
                action: {
                  type: ActionType.Reminder,
                },
              };

              await createWorkflow({ input: workflowInput });
            }
          },
        }
      );
    } catch (error) {
      // Handle submission errors silently
    } finally {
      setIsSubmitting(false);
    }
  };

  // Pre-fill for the quick-reminder time picker, held stable for the form's
  // lifetime so an untouched value still compares equal at submit time — that
  // equality is what keeps atTime/timeZone off the payload (default-driven).
  const quickReminderTimeBaseline = useMemo(
    () => getScheduleDefaultInstant(SCHEDULE_DEFAULTS.reminder),
    []
  );

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { isDirty, isValid },
  } = useForm({
    resolver: yupResolver(NewIssueReportFormSchema),
    mode: 'onChange',
    defaultValues: {
      message: '',
      title: '',
      activityType: Severity.Red, // Default to Urgent
      labels: [],
      quickReminderDate: undefined,
      quickReminderTime: quickReminderTimeBaseline,
      changeDisplayDate: undefined,
      adjustCreationDate: undefined,
    },
  });

  // Guard to prevent the two-way sync from creating an infinite loop
  const isSettingFromParent = useRef(false);

  // Sync external selectedLabels into form
  useEffect(() => {
    if (props.selectedLabels) {
      isSettingFromParent.current = true;
      setValue('labels', props.selectedLabels as any, { shouldDirty: false });
    }
  }, [props.selectedLabels]); // eslint-disable-line react-hooks/exhaustive-deps

  // Notify parent when labels change (skip if the change came from parent sync)
  const watchedLabels = watch('labels');
  useEffect(() => {
    if (isSettingFromParent.current) {
      isSettingFromParent.current = false;
      return;
    }
    if (props.onLabelsChange && watchedLabels) {
      props.onLabelsChange(watchedLabels as any);
    }
  }, [watchedLabels]); // eslint-disable-line react-hooks/exhaustive-deps

  const onResetForm = () => {
    reset();
    setIssueMedia([]);
  };

  if (!props.bottomBar) {
    return (
      <KeyboardSafeView style={styles.container}>
        <SaveFormTop
          isDirty={isDirty && !isPending}
          isValid={isValid && !isUploading && !isPending && !isSubmitting}
          onSubmit={async () => {
            await handleSubmit(onSubmit)();
          }}
          title={issueTypeTitle}
          saveLabel={t('issue.new.submit')}
          topBar={props.topBar}
          onCancel={props.onCancel}
        >
          <NewIssueReportForm
            workspaceId={workspaceId}
            mediaGallery={HorizontalMediaGallery}
            control={control}
            setValue={setValue}
            watch={watch}
            mediaPicker={MediaPicker}
            addMedia={addMedia}
            deleteMedia={deleteMedia}
            setIsUploading={setIsUploading}
            acceptedMediaTypes={acceptedMediaTypes}
            availableLabels={availableLabels}
            onCreateLabel={handleCreateLabel}
            hideAdvancedSettings={props.hideAdvancedSettings}
            pickerMode={props.pickerMode}
            onSelectorEdit={props.onSelectorEdit}
          />
        </SaveFormTop>
      </KeyboardSafeView>
    );
  }

  return (
    <View style={styles.containerCropCircle}>
      <SaveFormBottom
        isDirty={isDirty && !isPending}
        isValid={isValid && !isUploading && !isPending && !isSubmitting}
        onSubmit={async () => {
          await handleSubmit(onSubmit)();
        }}
        title={issueTypeTitle}
        saveLabel={t('issue.new.submit')}
        bottomBar={props.bottomBar}
        onCancel={props.onCancel}
        onResetForm={onResetForm}
      >
        <NewIssueReportForm
          workspaceId={workspaceId}
          mediaGallery={HorizontalMediaGallery}
          control={control}
          setValue={setValue}
          watch={watch}
          mediaPicker={MediaPicker}
          addMedia={addMedia}
          deleteMedia={deleteMedia}
          setIsUploading={setIsUploading}
          acceptedMediaTypes={acceptedMediaTypes}
          availableLabels={availableLabels}
          onCreateLabel={handleCreateLabel}
          hideAdvancedSettings={props.hideAdvancedSettings}
          pickerMode={props.pickerMode}
          onSelectorEdit={props.onSelectorEdit}
        />
      </SaveFormBottom>
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  container: {
    ...Layout.container,
    backgroundColor: role.surfaceHover,
    borderRadius: 12,
  },
  containerCropCircle: {
    ...Layout.container,
    backgroundColor: role.canvas,
    borderRadius: 12,
    position: 'relative',
  },
}));
